import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAdminOrders,
  fetchAdminProducts,
  fetchCustomers,
  fetchDashboardStats,
  getProductStock,
} from '../lib/adminApi';
import { formatPrice } from '../../utils/formatPrice';

function BarChart({ items, valueKey, labelKey, maxBars = 6 }) {
  const data = items.slice(0, maxBars);
  const max = Math.max(...data.map(item => Number(item[valueKey]) || 0), 1);

  if (!data.length) {
    return <p className="admin-empty">No data yet.</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {data.map(item => {
        const value = Number(item[valueKey]) || 0;
        const width = `${Math.max((value / max) * 100, 4)}%`;
        return (
          <div key={item[labelKey] || item.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span>{item[labelKey]}</span>
              <span style={{ color: '#777' }}>{item.displayValue ?? value}</span>
            </div>
            <div style={{ height: 10, background: '#f2f2f2', borderRadius: 999 }}>
              <div
                style={{
                  width,
                  height: '100%',
                  borderRadius: 999,
                  background: '#111',
                  transition: 'width 0.35s ease',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Analytics() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [statsData, ordersData, productsData, customersData] = await Promise.all([
          fetchDashboardStats().catch(() => null),
          fetchAdminOrders(),
          fetchAdminProducts(),
          fetchCustomers(),
        ]);
        if (cancelled) return;
        setStats(statsData);
        setOrders(ordersData || []);
        setProducts(productsData || []);
        setCustomers(customersData || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load analytics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    const interval = setInterval(load, 45000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const bestSellers = useMemo(() => {
    const counts = new Map();
    orders.forEach(order => {
      (order.order_items || []).forEach(item => {
        const key = item.product_name;
        const current = counts.get(key) || { name: key, quantity: 0, revenue: 0 };
        current.quantity += item.quantity;
        current.revenue += Number(item.price) * item.quantity;
        counts.set(key, current);
      });
    });
    return Array.from(counts.values())
      .sort((a, b) => b.quantity - a.quantity)
      .map(item => ({
        ...item,
        displayValue: `${item.quantity} sold`,
      }));
  }, [orders]);

  const lowStock = useMemo(
    () =>
      products
        .map(p => ({ ...p, stock: getProductStock(p) }))
        .filter(p => p.stock.available <= 5)
        .sort((a, b) => a.stock.available - b.stock.available)
        .slice(0, 8),
    [products]
  );

  const revenue = stats?.revenue ?? orders.reduce((sum, o) => sum + Number(o.total || 0), 0);
  const recentCustomers = [...customers]
    .sort((a, b) => new Date(b.last_order_at) - new Date(a.last_order_at))
    .slice(0, 6);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="admin-page-title">Analytics</h1>
        <p className="admin-page-subtitle">Auto-refreshes every 45 seconds</p>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {loading && <p className="admin-empty">Loading analytics...</p>}

      {!loading && (
        <>
          <div className="admin-stats">
            <div className="admin-stat">
              <p className="admin-stat-label">Revenue</p>
              <p className="admin-stat-value">{formatPrice(revenue)}</p>
            </div>
            <div className="admin-stat">
              <p className="admin-stat-label">Orders</p>
              <p className="admin-stat-value">{stats?.orders_count ?? orders.length}</p>
            </div>
            <div className="admin-stat">
              <p className="admin-stat-label">New customers</p>
              <p className="admin-stat-value">{customers.length}</p>
            </div>
            <div className="admin-stat">
              <p className="admin-stat-label">Low stock SKUs</p>
              <p className="admin-stat-value">{stats?.low_stock_count ?? lowStock.length}</p>
            </div>
          </div>

          <div className="admin-grid-2">
            <div className="admin-card">
              <h2 className="admin-card-title">Best sellers</h2>
              <BarChart items={bestSellers} valueKey="quantity" labelKey="name" />
            </div>

            <div className="admin-card">
              <h2 className="admin-card-title">Recent orders</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 8).map(order => (
                      <tr key={order.id}>
                        <td>
                          <Link to={`/admin/orders/${order.id}`}>
                            {order.order_number || order.id.slice(0, 8)}
                          </Link>
                        </td>
                        <td>{order.shipping_name}</td>
                        <td>{formatPrice(order.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!orders.length && <p className="admin-empty">No recent orders.</p>}
              </div>
            </div>
          </div>

          <div className="admin-grid-2" style={{ marginTop: 16 }}>
            <div className="admin-card">
              <h2 className="admin-card-title">Low stock products</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStock.map(product => (
                      <tr key={product.id}>
                        <td>
                          <Link to={`/admin/products/${product.id}`}>{product.name}</Link>
                        </td>
                        <td>{product.stock.available}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!lowStock.length && <p className="admin-empty">Inventory looks healthy.</p>}
              </div>
            </div>

            <div className="admin-card">
              <h2 className="admin-card-title">New customers</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCustomers.map(customer => (
                      <tr key={customer.id}>
                        <td>{customer.name}</td>
                        <td>{customer.email}</td>
                        <td>{customer.orders_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!recentCustomers.length && <p className="admin-empty">No customers yet.</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Analytics;
